import { Button, Container, Stack, Typography } from "@mui/material";
import moment from "moment";
import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { useRef } from "react";
import { createBlobInContainer } from "src/utils/azureBlob";
import { del, get, post, put } from "src/utils/networkUtils";
import { useLocation } from "react-router-dom";
import Iconify from "src/components/iconify";
import PrizeDrawForm from "src/components/prize-draw-admin/PrizeDrawForm";

const dateFormat = "YYYY-MM-DD HH:mm:ss";

export default function PrizeDrawAddPage() {
  const fileInputRef = useRef(null);
  const location = useLocation();
  const prizeDrawParams = location.state?.prizeDraw;
  const [loadingDeletePrizeIds, setLoadingDeletePrizeIds] = useState([]);
  const [prizeToggle, setPrizeToggle] = useState(false);
  const [prizeLoading, setPrizeLoading] = useState(false);
  const [prizeDrawBody, setPrizeDrawBody] = useState({
    title: "",
    drawDate: moment().format(dateFormat),
    submissionStartDate: moment().format(dateFormat),
    submissionEndDate: moment().format(dateFormat),
    prizeText: [],
    postalEntry: "",
    tAndCs: "",
    winnersMessage: "",
    maxTicketEntry: "",
    image: "",
    status: "draft",
    isFeatured: false,
    isCompleted: false,
  });
  const postalEntryRef = useRef(null);
  const tAndCsRef = useRef(null);
  const winnersMsgRef = useRef(null);
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (prizeDrawParams) {
      get(`/admin/prize-draw/${prizeDrawParams.prizeDrawId}`).then(function (
        response
      ) {
        const resp = response?.prizeDraw;
        if (resp !== null && resp !== undefined) {
          const updated = {
            ...resp,
            maxTicketEntry:
              resp.maxTicketEntry === -1 ? "" : resp.maxTicketEntry,
            prizeText: resp?.prizes?.map((item) => {
              return { title: item.Title, id: item.PrizeId };
            }),
          };
          setPrizeDrawBody(updated);
        }
      });
    }
  }, [prizeToggle, prizeDrawParams]);

  useEffect(() => {
    if (prizeDrawParams) {
      const updated = {
        ...prizeDrawParams,
        maxTicketEntry:
          prizeDrawParams.maxTicketEntry === -1
            ? ""
            : prizeDrawParams.maxTicketEntry,
        prizeText: prizeDrawParams?.prizes?.map((item) => {
          return { title: item.Title, id: item.PrizeId };
        }),
      };
      setPrizeDrawBody(updated);
    }
  }, [prizeDrawParams]);

  useEffect(() => {
    if (prizeDrawParams) return;
    get(`/admin/prize-draws-details`)
      .then(function (response) {
        setPrizeDrawBody((prev) => ({
          ...prev,
          postalEntry: response.prizeDrawDetails.postalEntry,
          tAndCs: response.prizeDrawDetails.tAndCs,
          winnersMessage: response.prizeDrawDetails.winnersMessage,
        }));
      })
      .catch(function (error) {
        console.error("Error fetching prize draw details:", error);
      });
  }, [prizeDrawParams]);

  const handleButtonClick = () => {
    fileInputRef.current?.click(); // triggers file input dialog
  };

  const handleChangeStatus = (event) => {
    setPrizeDrawBody((prevState) => ({
      ...prevState,
      status: event.target.value,
    }));
  };

  const handlePrizeChange = (value, prizeId) => {
    setPrizeDrawBody((prev) => ({
      ...prev,
      prizeText: prev.prizeText.map((item) =>
        item.id === prizeId ? { ...item, title: value } : item
      ),
    }));
  };

  const handleRemovePrizeRow = async (prizeId) => {
    const idString = prizeId.toString();
    const isNewAdded = idString.includes("temp-");
    if (isNewAdded) {
      setPrizeDrawBody((prev) => ({
        ...prev,
        prizeText: prev.prizeText.filter((item) => item.id !== prizeId),
      }));
    }
    if (!isNewAdded) {
      setLoadingDeletePrizeIds((prev) => [...prev, prizeId]);
      try {
        const resp = await del(
          `/admin/prize/${prizeId}/${prizeDrawParams.prizeDrawId}`
        );
        if (resp.success) {
          setPrizeToggle(!prizeToggle);
        }
      } catch (error) {
        setLoadingDeletePrizeIds((prev) => prev.filter((id) => id !== prizeId));
      } finally {
        setLoadingDeletePrizeIds((prev) => prev.filter((id) => id !== prizeId));
      }
    }
  };

  const handleAddPrizeRow = () => {
    setPrizeDrawBody((prev) => ({
      ...prev,
      prizeText: [...prev.prizeText, { title: "", id: `temp-${Date.now()}` }],
    }));
  };
  const handleDateChange = (newValue, fieldName) => {
    setPrizeDrawBody((prevState) => ({
      ...prevState,
      [fieldName]: moment(newValue).format(dateFormat),
    }));
  };
  const uploadFiles = (event) => {
    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/gif"];
      const maxSize = 2 * 1024 * 1024;

      if (!validTypes.includes(file.type)) {
        alert("Only JPG, JPEG, PNG, WEBP, and GIF formats are allowed.");
        return;
      }

      if (file.size > maxSize) {
        alert("File size should not exceed 2MB.");
        return;
      }
      const newFileName = file.name.replace(/\s+/g, "_");
      const renamedFile = new File([file], newFileName, {
        type: file.type,
      });
      let uploadPromises = [];
      uploadPromises.push(createBlobInContainer(renamedFile));

      if (uploadPromises.length > 0) {
        const uploadFiles = async () => {
          await Promise.all(uploadPromises);

          const containerName = process.env.REACT_APP_STORAGE_CONTAINER;
          const storageAccountName = process.env.REACT_APP_STORAGERESOURCENAME;
          const imageUrl = `https://${storageAccountName}.blob.core.windows.net/${containerName}/${renamedFile.name}`;
          setPrizeDrawBody({ ...prizeDrawBody, image: imageUrl });
        };

        uploadFiles().catch(console.error);
      }
    }
  };

  const handleSubmit = async () => {
    if (
      !prizeDrawBody.submissionStartDate ||
      !prizeDrawBody.submissionEndDate ||
      !prizeDrawBody.drawDate ||
      !prizeDrawBody.title ||
      !prizeDrawBody.image ||
      !prizeDrawBody.prizeText.length
    ) {
      alert("Please fill in all required fields");
      return;
    }
    const startDate = new Date(prizeDrawBody.submissionStartDate);
    const endDate = new Date(prizeDrawBody.submissionEndDate);
    const prizeDrawDate = new Date(prizeDrawBody.drawDate);
    const currentDate = new Date();

    if (startDate >= endDate) {
      alert("Submission end date must be after start date");
      return;
    }

    if (prizeDrawDate <= endDate) {
      alert("Draw date must be after submission end date");
      return;
    }

    if (prizeDrawParams) {
      if (
        prizeDrawParams.title?.trim().toLowerCase() !==
        prizeDrawBody.title?.trim().toLowerCase()
      ) {
        await get(`/admin/prize-draws/unique/${prizeDrawBody.title}`);
      }
      await put(
        `/admin/prize-draws/${prizeDrawParams.prizeDrawId}`,
        prizeDrawBody
      );
      window.location.href = "/dashboard/prize-draws";
    } else {
      if (prizeDrawDate < currentDate) {
        alert("Draw date cannot be in the past");
        return;
      }
      const response = await post(`/admin/prize-draws`, prizeDrawBody);
      if (response.success) {
        window.location.href = "/dashboard/prize-draws";
      }
    }
  };

  const handleChangeFeatured = (event) => {
    setPrizeDrawBody((prevState) => ({
      ...prevState,
      isFeatured: event.target.value,
    }));
  };

  const handlePrizeSubmit = async ({ id, title }) => {
    try {
      setPrizeLoading(true);
      const idString = id.toString();
      const isNewAdded = idString.includes("temp-");
      if (!isNewAdded) {
        const resp = await put(`/admin/prize/${id}`, {
          prizeText: title,
          prizeDrawId: prizeDrawParams.prizeDrawId,
        });
        if (resp.success) {
          setPrizeToggle(!prizeToggle);
        }
      } else {
        const resp = await post(`/admin/prize/add`, {
          prizeText: title,
          prizeDrawId: prizeDrawParams.prizeDrawId,
        });
        if (resp.success) {
          setPrizeToggle(!prizeToggle);
        }
      }
    } catch (error) {
      setPrizeLoading(false);
    } finally {
      setPrizeLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{`${prizeDrawParams ? "Update" : "Create"} Prize Draw`}</title>
      </Helmet>
      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4">{`${prizeDrawParams ? "Update" : "Create"
            } Prize Draw`}</Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() =>
              (window.location.href =
                "/dashboard/prize-draws")
              }
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              startIcon={<Iconify icon="eva:save-fill" />}
              sx={{ mr: 1 }}
            >
              Save
            </Button>
          </Stack>
        </Stack>
        <PrizeDrawForm
          prizeDrawBody={prizeDrawBody}
          setPrizeDrawBody={setPrizeDrawBody}
          handleDateChange={handleDateChange}
          handleChangeFeatured={handleChangeFeatured}
          handleChangeStatus={handleChangeStatus}
          fileInputRef={fileInputRef}
          handleButtonClick={handleButtonClick}
          uploadFiles={uploadFiles}
          handlePrizeChange={handlePrizeChange}
          handlePrizeSubmit={handlePrizeSubmit}
          handleRemovePrizeRow={handleRemovePrizeRow}
          handleAddPrizeRow={handleAddPrizeRow}
          loadingDeletePrizeIds={loadingDeletePrizeIds}
          prizeDrawParams={prizeDrawParams}
          prizeLoading={prizeLoading}
          postalEntryRef={postalEntryRef}
          tAndCsRef={tAndCsRef}
          winnersMsgRef={winnersMsgRef}
        />
      </Container>
    </>
  );
}
